import { DateTime, Duration } from "luxon"
import ROOT from "./root"
import {
  ConnectionError,
  UnauthorizedError,
  NotAllowedError,
  InvalidParametersError,
  ServerError,
  IAPinUseError,
  AnalysisUpdatedError,
  RequestError,
  DoNotShowError,
} from "./error"

DateTime.prototype.toJSON = function () {
  throw new Error("Automatic conversion of datetimes to JSON is disabled")
}

Duration.prototype.toJSON = function () {
  throw new Error("Automatic conversion of durations to JSON is disabled")
}

// Track active requests to abort them on page unload
const activeRequests = new Set()

// Flag to track if page is being unloaded
let isPageUnloading = false

// Abort all active requests when page is being unloaded
window.addEventListener('beforeunload', () => {
  isPageUnloading = true
  activeRequests.forEach(controller => {
    try {
      controller.abort('Page unload')
    } catch (err) {
      // Ignore errors when aborting - the controller might already be aborted
    }
  })
  activeRequests.clear()
})

export default function request(method, url, data = undefined, isJson = true, login = false) {
  const auth = window.store.getState().auth
  
  // Create AbortController for this request
  const controller = new AbortController()
  activeRequests.add(controller)

  let options = {
    method: method,
    credentials: login ? "include" : "same-origin",
    headers: {},
    signal: controller.signal,
  }

  if (auth.isLoggedIn) {
    options.headers["Authorization"] = `Bearer ${auth.accessToken}`
  }
  if (data !== undefined) {
    if (isJson) {
      options.body = JSON.stringify(data)
      options.headers["Content-Type"] = "application/json"
    } else {
      options.body = data
    }
  }

  const cleanupController = () => {
    activeRequests.delete(controller)
  }

  return window
    .fetch(ROOT + url, options)
    .catch((err) => {
      // Clean up the controller from active requests
      cleanupController()
      
      // Check if the request was aborted or if page is unloading
      if (err.name === 'AbortError' || controller.signal.aborted || isPageUnloading) {
        // Create a special silent abort error
        const silentAbortError = new Error('Silent abort')
        silentAbortError.name = 'SilentAbortError'
        throw silentAbortError
      }
      
      // Give the generic `TypeError` that window.fetch returns a more specific name
      // We don't want to display every time the server is down to the user
      if (url === "/team-status") throw new DoNotShowError("Do Not Show Error")
      throw new ConnectionError("Failed contacting server. Please check your internet connection!")
    })
    .then((res) => {
      // Clean up the controller from active requests on success
      cleanupController()
      
      // This is expected to have an empty body
      if (res.status === 204) {
        return [res.status, null]
      }
      // Only decode JSON if the content type says so
      if (res.headers.get("Content-Type") !== "application/json") {
        return [res.status, null]
      }
      // We need to use Promise.all, since res.json is a promise
      // This is done now, so that the error handling further down can use the result
      return Promise.all([res.status, res.json()])
    })
    .then(([status, json]) => {
      // Check if the request was aborted - throw silent abort error
      if (controller.signal.aborted || isPageUnloading) {
        const silentAbortError = new Error('Silent abort')
        silentAbortError.name = 'SilentAbortError'
        throw silentAbortError
      }
      
      // In here, we basically use use the json.message provided by the BE, if available
      // Note: The user only sees the `message` parameter, the error type is hidden!
      const message = json && json.message

      if (status === 401) {
        throw new UnauthorizedError(message || "Unauthorized")
      }
      if (status === 403) {
        throw new NotAllowedError(message || "The action you tried to perform is not allowed")
      }
      if (status === 404) {
        // TODO: Extract relevant data
        throw new RequestError(message || "Not found")
      }
      if (status === 422) {
        console.warn("Validation errors:", json && json.errors)
        // TODO: Use the message from the BE, or extract data from `json.errors``
        const errorMessage =  message !== "Failed validating request." ? message : false
        throw new InvalidParametersError(errorMessage || "Some data was invalid, please retry")
      }
      if (status === 409 || status === 419) {
        throw new IAPinUseError(message || "IAP is in use")
      }
      if (status === 410) {
        throw new AnalysisUpdatedError(message || "Analysis has been updated by another user, please refresh")
      }
      if (status >= 500) {
        // TODO: Attempt to extract data (maybe HTTP status title?)
        throw new ServerError(message || "A server error occurred")
      }
      if (status < 200 || status >= 300) {
        throw new RequestError(message || "An unknown error occurred")
      }

      return json
    })
    .catch((err) => {
      // Final cleanup - ensure controller is always removed
      cleanupController()
      
      // Don't log silent abort errors
      if (err.name === 'SilentAbortError' || err.name === 'AbortError') {
        throw err
      }
      
      // Log other errors
      console.error(err)
      throw err
    })
}
